import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';

import { useButton, useI18n, useLogger, useSelector } from '~Hooks';
import { BlueButton, Box } from '~Styles';
import { Input, SearchInputWrapper } from '~Styles/inputs';
import BackTopBar from '~ComponentsImproved/BackTopBar';

import MembersList from './Components/MembersList';
import MemberDetails from './Components/MemberDetails';
import MemberDetailsSkeleton from './Components/MemberDetailsSkeleton';
import useModal from '~ComponentsImproved/Modal/useModal';
import Modal from '~ComponentsImproved/Modal';
import CreateNewUser from '~Components/LinkUser/CreateNewUser';

const Wrapper = styled(Box)`
  flex: 1;
  height: calc(100vh - 64px);
  background-color: var(--colorBlueGray1200);
  color: var(--colorWhite);
`;

const ScrollWrapper = styled(Box)`
  overflow-y: hidden;
  height: 100%;
  flex: 1;
  padding: 20px 40px 0px 20px;
  box-sizing: border-box;
  opacity: 0;
  animation: fadeIn 0.2s ease-in-out 0.1s forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ListColumn = styled(Box)`
  flex: 1;
  overflow: hidden;
`;

const CreateNewUserButton = styled(BlueButton)`
  white-space: nowrap;
  width: 140px;
`;

const ModalContent = styled.div`
  padding: 24px;
  background: var(--colorBlueGray1200);
  border-radius: 8px;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.2);
  width: 600px;
  max-width: calc(100% - 64px);
  margin: 0;
  height: fit-content;
  max-height: calc(100vh - 16px);
  overflow-y: auto;

  /** Push modal up so iPad keyboard doesn't cover the input field */
  @media (min-height: 496px) {
    max-height: none;
    overflow-y: visible;
    margin: 16px 0 0 0;
  }

  @media (min-height: 680px) {
    margin: 16px 0 10% 0;
  }
`;

/** Main page for managing members */
const Members: FC = () => {
  const { i18n } = useI18n();
  const logger = useLogger('members');

  const createUserModal = useModal();

  const country = useSelector((state) => state.country);

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchValue]);

  const createUserButton = useButton({
    onClick: () => createUserModal.toggle(),
  });

  return (
    <>
      <Wrapper display="flex" flexDirection="column">
        <BackTopBar title={i18n('Members')} dark>
          {country === 'ES' && (
            <>
              <CreateNewUserButton size="small" {...createUserButton} marginRight="small">
                {i18n('CustomerForCheckout.CreateNewCustomer')}
              </CreateNewUserButton>
            </>
          )}
        </BackTopBar>

        <ScrollWrapper display="flex" flexDirection="row" gap="large">
          <ListColumn display="flex" flexDirection="column" gap="small">
            <SearchInputWrapper value={searchValue} onClear={() => setSearchValue('')} fluid>
              <Input
                as="input"
                dark
                fluid
                isSearch
                value={searchValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchValue(e.target.value)
                }
                placeholder={i18n('Invoice.SearchUser')}
              />
            </SearchInputWrapper>

            <MembersList
              debouncedSearchValue={debouncedSearchValue}
              selectedMemberId={selectedMemberId}
              onSelectMember={setSelectedMemberId}
            />
          </ListColumn>

          {selectedMemberId ? (
            <MemberDetails userId={selectedMemberId} />
          ) : (
            <MemberDetailsSkeleton />
          )}
        </ScrollWrapper>
      </Wrapper>

      <Modal isShowing={createUserModal.isShowing} hide={createUserModal.toggle}>
        <ModalContent>
          <CreateNewUser
            onUserCreated={(id) => {
              logger.info('New user created', { userId: id });
              setSelectedMemberId(id);
              createUserModal.toggle();
            }}
            onCancel={() => {
              createUserModal.toggle();
            }}
          />
        </ModalContent>
      </Modal>
    </>
  );
};

export default Members;
